import DoubleClickZoom from 'ol/interaction/DoubleClickZoom.js';
import { POI_ANALYSIS_ACTIONS, POI_ANALYSIS_MENU_ACTION } from './poiAnalysisActions.js';
import { openProximityAnalysisDialog } from './proximityAnalysisDialog.js';

const HIT_TOLERANCE = 14;
let initialized = false;
let menuElement = null;
let hovered = null;
let selected = null;
let activeMap = null;
let doubleClickZoom = null;
let hoverTimer = null;
let lastPointerPosition = null;

function setZoomGuard(active) { doubleClickZoom?.setActive?.(!active); }

function getPoiHit(map, pixel) {
  let hit = null;
  map.forEachFeatureAtPixel(pixel, (feature, layer) => {
    if (!layer?.get?.('poiSetId') || layer.getVisible?.() === false) return undefined;
    hit = { feature, layer }; return true;
  }, { hitTolerance: HIT_TOLERANCE, layerFilter: layer => !!layer?.get?.('poiSetId') && layer.getVisible?.() });
  return hit;
}

function getSearchCompanyHit(map, pixel) {
  return map.forEachFeatureAtPixel(pixel, (feature, layer) => {
    if (String(feature.get?.('type') || '').trim().toLowerCase() !== 'company') return undefined;
    return { feature, layer, targetType: 'poi', searchCompany: true };
  }, {
    hitTolerance: HIT_TOLERANCE,
    layerFilter: layer => layer?.getVisible?.() !== false
      && (layer?.get?.('searchLocationMarker') === true || layer?.get?.('searchPoiCategory') === true)
  });
}

function getHighlightStyle(hit) { return hit?.layer?.get?.('poiFeatureInfoStyle') || hit?.layer?.getStyle?.(); }
function cancelHoverTimer() { window.clearTimeout(hoverTimer); hoverTimer = null; }
function hideMenu() { if (menuElement) menuElement.style.display = 'none'; }
function restoreFeature(hit) { if (hit?.feature && hit.feature !== selected?.feature) hit.feature.setStyle(undefined); }

function clearHover() {
  cancelHoverTimer(); restoreFeature(hovered); hovered = null;
  activeMap?.getViewport?.().classList.remove('poi-near-pointer');
  if (!selected) setZoomGuard(false);
}

function clearSelection() {
  cancelHoverTimer(); selected?.feature?.setStyle?.(undefined); selected = null;
  hideMenu(); clearHover(); setZoomGuard(false);
}

function highlightCandidate(hit) {
  if (!hit || selected?.feature === hit.feature) return false;
  const changed = hovered?.feature !== hit.feature;
  if (changed) { cancelHoverTimer(); restoreFeature(hovered); hit.feature.setStyle(getHighlightStyle(hit)); }
  hovered = hit; setZoomGuard(true); return changed;
}

function positionMenu(clientX, clientY) {
  menuElement.style.display = 'block'; menuElement.style.visibility = 'hidden';
  const margin = 10, gap = 12, width = menuElement.offsetWidth || 200, height = menuElement.offsetHeight || 130;
  menuElement.style.left = `${Math.max(margin, Math.min(clientX + gap, window.innerWidth - width - margin))}px`;
  menuElement.style.top = `${Math.max(margin, Math.min(clientY + gap, window.innerHeight - height - margin))}px`;
  menuElement.style.visibility = 'visible'; menuElement.querySelector('button')?.focus({ preventScroll: true });
}

function updateHeadingIcon(iconSrc, categoryColor) {
  const container = menuElement?.querySelector('.poi-context-menu-heading-icon');
  const image = menuElement?.querySelector('.poi-context-menu-heading-icon img');
  const fallback = menuElement?.querySelector('.poi-context-menu-heading-icon i');
  if (image) {
    image.hidden = !iconSrc;
    image.src = iconSrc || '';
  }
  if (fallback) fallback.hidden = !!iconSrc;
  container?.classList.toggle('has-category-dot', !!categoryColor);
  container?.style.setProperty('--poi-category-color', categoryColor || 'transparent');
}

function selectPoi(hit, screenPosition = lastPointerPosition) {
  if (!hit || !screenPosition) return;
  cancelHoverTimer();
  if (selected?.feature && selected.feature !== hit.feature) selected.feature.setStyle(undefined);
  hit.feature.setStyle(getHighlightStyle(hit));
  selected = { ...hit, targetType: 'poi', coordinate: hit.feature.getGeometry?.().getCoordinates?.() };
  hovered = hit;
  const locationName = hit.feature.get?.('name') || hit.feature.get?.('label') || hit.feature.get?.('title') || hit.feature.get?.('identifier') || 'Selected location';
  const setName = hit.layer.get?.('poiSetName') || hit.layer.get?.('title') || 'Point of Interest Set';
  menuElement.querySelector('.poi-context-location-name').textContent = String(locationName);
  menuElement.querySelector('.poi-context-set-name').textContent = String(setName);
  const categoryColor = hit.layer.get?.('poiHasConfiguredIcon') ? hit.layer.get?.('poiStyleColor') : null;
  updateHeadingIcon(hit.layer.get?.('poiLegendIconSrc'), categoryColor);
  setZoomGuard(true); positionMenu(screenPosition.clientX, screenPosition.clientY);
}

function createMenu() {
  const menu = document.createElement('div');
  menu.className = 'poi-context-menu'; menu.setAttribute('role', 'menu'); menu.setAttribute('aria-label', 'Point of Interest actions');
  Object.assign(menu.style, { position: 'fixed', display: 'none', zIndex: '10000' });
  menu.addEventListener('contextmenu', event => event.preventDefault());
  menu.addEventListener('pointerdown', event => event.stopPropagation());
  menu.addEventListener('pointerleave', clearSelection);
  const heading = document.createElement('div');
  heading.className = 'poi-context-menu-heading';
  heading.innerHTML = '<span class="poi-context-menu-heading-icon"><img alt="" hidden><i class="bi bi-geo-alt-fill" aria-hidden="true"></i></span><span class="poi-context-menu-heading-copy"><strong class="poi-context-location-name"></strong><small class="poi-context-set-name"></small></span>';
  const button = document.createElement('button');
  button.type = 'button'; button.className = 'poi-context-menu-item'; button.setAttribute('role', 'menuitem');
  button.innerHTML = `<i class="bi ${POI_ANALYSIS_MENU_ACTION.icon}" aria-hidden="true"></i><span>${POI_ANALYSIS_MENU_ACTION.label}</span>`;
  button.onclick = event => { event.preventDefault(); event.stopPropagation(); if (selected) { hideMenu(); openProximityAnalysisDialog(POI_ANALYSIS_ACTIONS[0], selected); } };
  menu.append(heading, button);
  document.body.appendChild(menu); return menu;
}

export function setupPoiContextMenu(map) {
  if (initialized || !map) return;
  initialized = true; activeMap = map; menuElement = createMenu();
  doubleClickZoom = map.getInteractions?.().getArray().find(interaction => interaction instanceof DoubleClickZoom) || null;
  map.on('pointermove', event => {
    if (event.dragging || selected) return;
    lastPointerPosition = { clientX: event.originalEvent?.clientX ?? 0, clientY: event.originalEvent?.clientY ?? 0 };
    const hit = getPoiHit(map, event.pixel);
    if (!hit) { clearHover(); return; }
    highlightCandidate(hit); map.getViewport().classList.add('poi-near-pointer');
  });
  map.on('click', event => {
    if (event.originalEvent?.button !== 0) return;
    const hit = getPoiHit(map, event.pixel);
    if (!hit) return;
    const originalEvent = event.originalEvent;
    lastPointerPosition = { clientX: originalEvent?.clientX ?? 0, clientY: originalEvent?.clientY ?? 0 };
    selectPoi(hit, lastPointerPosition);
  });
  const openMenu = event => {
    const pixel = map.getEventPixel(event);
    const companyHit = getSearchCompanyHit(map, pixel);
    if (companyHit) {
      event.preventDefault(); event.stopPropagation();
      if (event.type === 'contextmenu') {
        clearSelection();
        openProximityAnalysisDialog(POI_ANALYSIS_ACTIONS[0], companyHit);
      }
      return;
    }
    const hit = getPoiHit(map, pixel) || hovered;
    if (!hit) return;
    event.preventDefault(); event.stopPropagation(); lastPointerPosition = { clientX: event.clientX, clientY: event.clientY }; selectPoi(hit);
  };
  map.getViewport().addEventListener('pointerdown', event => { if (event.button === 2) openMenu(event); }, true);
  map.getViewport().addEventListener('contextmenu', openMenu, true);
  map.on('movestart', clearSelection);
  document.addEventListener('application-reset-active-tools', clearSelection);
  window.addEventListener('blur', clearSelection);
}
